import { Component } from "@theme/component";
import { fetchConfig, startViewTransition } from "@theme/utilities";
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { getCart } from "@theme/cart-state";

/**
 * Maximum quantity limit for add-ons
 * @constant {number}
 */
const MAX_QTY_LIMIT = 10;

/** Selector for the product grid */
const GRID_SELECTOR = "#addons-product-grid";

/** Selector for results count */
const RESULTS_COUNT_SELECTOR = "[data-results-count]";

/**
 * Reusable price formatter (creating Intl.NumberFormat is expensive)
 */
const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

/**
 * Formats a price in cents to a currency string
 * @param {number} cents - Price in cents
 * @returns {string} Formatted price string (e.g., "$12.99")
 */
function formatPrice(cents) {
  return priceFormatter.format(cents / 100);
}

/**
 * Add-ons page controller component
 * Handles cart operations (add to cart, quantity changes) and UI state
 *
 * Note: Filtering and sorting are handled by facets-form-component using S&D + section rendering
 *
 * @typedef {Object} AddonsPageRefs
 * @property {HTMLElement} cartTotal - Cart total display
 *
 * @extends {Component<AddonsPageRefs>}
 */
class AddonsPageComponent extends Component {
  /** @type {Map<string, {lineKey: string, quantity: number}>} */
  #cartAddons = new Map();

  /** @type {string | null} */
  #loadingVariantId = null;

  /** @type {boolean} Prevents concurrent cart syncs */
  #isSyncing = false;

  /** @type {AbortController | null} */
  #filterAbortController = null;

  /** @type {boolean} */
  #hasTrackedView = false;

  connectedCallback() {
    super.connectedCallback();

    // Listen for cart updates from elsewhere
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    // Intercept facets-form-component to use grid-only morphing
    this.#setupFacetsIntercept();

    // Initial cart sync - but protect against stale data overwriting server-rendered total
    this.#syncCartState({ preserveTotalIfStale: true });

    if (!this.#hasTrackedView) {
      this.#hasTrackedView = true;
      window.grAnalytics?.trackAddonSelectionViewed({});
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#filterAbortController?.abort();
  }

  /**
   * Intercepts facets-form-component instances within this component
   * to override their section update behavior with grid-only morphing
   */
  #setupFacetsIntercept() {
    const facetsForms = this.querySelectorAll("facets-form-component");

    facetsForms.forEach((facetsForm) => {
      // Override the updateFilters method to use our grid-only approach
      const originalUpdateFilters = facetsForm.updateFilters?.bind(facetsForm);

      if (typeof originalUpdateFilters === "function") {
        facetsForm.updateFilters = () => {
          // Let facets-form-component update the URL
          this.#updateURLFromFacetsForm(facetsForm);

          // Then do grid-only morph instead of full section morph
          this.#updateProductGridOnly();
        };
      }

      // Add listeners to optimistically update filter/sort pill visuals
      facetsForm.addEventListener("change", this.#onFilterInputChange);
    });
  }

  /**
   * Handles filter/sort input changes to optimistically update pill visuals
   * @param {Event} event
   */
  #onFilterInputChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.type === "checkbox") {
      this.#updateFilterPillState(target);
    } else if (target.type === "radio" && target.name === "sort_by") {
      this.#updateSortPillStates(target);
    }
  };

  /**
   * Updates a filter pill's visual state based on checkbox state
   * Also syncs the same filter across desktop/mobile
   * @param {HTMLInputElement} checkbox
   */
  #updateFilterPillState(checkbox) {
    // Find all checkboxes with the same name and value (desktop + mobile)
    const allMatchingCheckboxes = this.querySelectorAll(
      `input[type="checkbox"][name="${checkbox.name}"][value="${checkbox.value}"]`,
    );

    allMatchingCheckboxes.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const label = this.querySelector(`label[for="${input.id}"]`);
      if (!(label instanceof HTMLLabelElement)) return;

      // Sync the checked state
      input.checked = checkbox.checked;

      if (checkbox.checked) {
        label.classList.add("addon-filter-pill--active");
        label.classList.remove("bg-gray-500");
      } else {
        label.classList.remove("addon-filter-pill--active");
        label.classList.add("bg-gray-500");
      }
    });
  }

  /**
   * Updates all sort pill visual states when selection changes
   * @param {HTMLInputElement} selectedRadio
   */
  #updateSortPillStates(selectedRadio) {
    // Update all sort pills in both desktop and mobile
    const allSortRadios = this.querySelectorAll('input[name="sort_by"]');

    allSortRadios.forEach((radio) => {
      if (!(radio instanceof HTMLInputElement)) return;

      const label = radio.closest("label");
      if (!label) return;

      const isSelected = radio.value === selectedRadio.value;

      if (isSelected) {
        label.classList.add("bg-blue-seafood", "scale-110");
        label.classList.remove("bg-gray-500");
        radio.checked = true;
      } else {
        label.classList.remove("bg-blue-seafood", "scale-110");
        label.classList.add("bg-gray-500");
        radio.checked = false;
      }
    });
  }

  /**
   * Updates URL based on facets form state (mirrors facets-form-component logic)
   * @param {Element} facetsForm
   */
  #updateURLFromFacetsForm(facetsForm) {
    const form = facetsForm.querySelector("form");
    if (!form) return;

    const formData = new FormData(form);
    const url = new URL(window.location.href);

    // Clear existing search params
    url.search = "";

    // Add form params
    for (const [key, value] of formData.entries()) {
      if (value) {
        url.searchParams.append(key, String(value));
      }
    }

    // Clean up empty price filters
    if (url.searchParams.get("filter.v.price.gte") === "")
      url.searchParams.delete("filter.v.price.gte");
    if (url.searchParams.get("filter.v.price.lte") === "")
      url.searchParams.delete("filter.v.price.lte");

    url.searchParams.delete("page");

    history.pushState({ urlParameters: url.search }, "", url.toString());
  }

  /**
   * Fetches the section and morphs only the product grid
   */
  async #updateProductGridOnly() {
    // Abort any pending request
    this.#filterAbortController?.abort();
    this.#filterAbortController = new AbortController();
    const signal = this.#filterAbortController.signal;

    const sectionId = this.getAttribute("section-id");
    if (!sectionId) return;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set("section_id", sectionId);

      const response = await fetch(url.toString(), { signal });
      if (!response.ok) throw new Error("Failed to fetch section");

      const html = await response.text();

      if (signal.aborted) return;

      // Parse response and extract just the product grid
      const fragment = new DOMParser().parseFromString(html, "text/html");
      const newGrid = fragment.querySelector(GRID_SELECTOR);
      const existingGrid = this.querySelector(GRID_SELECTOR);

      if (!newGrid || !existingGrid) {
        console.warn("[AddonsPage] Could not find product grid for morph");
        return;
      }

      // Also get results count if present
      const newResultsCount = fragment.querySelector(RESULTS_COUNT_SELECTOR);
      const existingResultsCount = this.querySelector(RESULTS_COUNT_SELECTOR);

      // Check for no-box redirect element in new HTML
      const noBoxElement = fragment.querySelector("[data-no-box-redirect]");
      if (noBoxElement instanceof HTMLElement) {
        const redirectUrl =
          noBoxElement.dataset.redirectUrl || "/collections/core-boxes";
        preserveRedirect(redirectUrl);
        return;
      }

      // Use view transition for smooth grid update
      startViewTransition(() => {
        morph(existingGrid, newGrid);

        if (newResultsCount && existingResultsCount) {
          existingResultsCount.textContent = newResultsCount.textContent;
        }

        // Sync card states after grid morph
        this.#syncCartState();
      }, ["product-grid"]);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      console.error("[AddonsPage] Failed to update product grid:", error);
    }
  }

  // CART OPERATIONS
  /**
   * Adds an add-on to cart
   * @param {{ variantId: string | number }} data
   * @param {Event} event
   */
  async addToCart({ variantId }, event) {
    // Ensure variantId is string (parseValue may convert to number)
    const variantIdStr = String(variantId);

    if (this.#loadingVariantId) return;

    // Find the button (event.target might be text inside the button)
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-action="add-to-cart"]');
    if (!(button instanceof HTMLButtonElement)) return;

    this.#loadingVariantId = variantIdStr;
    button.disabled = true;

    // Store current total for optimistic update fallback
    const currentTotalText = this.refs.cartTotal?.textContent?.trim();

    try {
      const body = JSON.stringify({
        items: [
          {
            id: variantIdStr,
            quantity: 1,
            properties: {
              _is_onetime_addon: "true",
            },
          },
        ],
      });

      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig("json", { body }),
      );

      if (!response.ok) {
        throw new Error("Failed to add to cart");
      }

      // Get the added item from the response
      const addResponse = await response.json();
      const addedItem = addResponse?.items?.[0];

      // ACQ-210 #2 (Phase C): post-add verify read. /cart/add.js returns the added line
      // item, NOT a full cart, so per the add=invalidate contract this cannot be primed —
      // it stays a forced network read. FORCE defeats the CDN cache but not Shopify
      // read-replica lag, so the stale-detection + optimistic fallback below are required.
      const cart = await getCart({ force: true });

      // Check if cart data appears stale
      const cartAppearsStale =
        cart?.items?.length === 0 ||
        (addedItem && !cart?.items?.some((item) => item.key === addedItem.key));

      if (cartAppearsStale && addedItem) {
        // Cart is stale - do optimistic update instead of resetting everything
        // Add the new item to our local state
        this.#cartAddons.set(variantIdStr, {
          lineKey: addedItem.key,
          quantity: addedItem.quantity,
        });

        // Update just this card's UI
        this.#updateCardState(variantIdStr);

        // Optimistically update total by adding the new item's price
        const addedPrice =
          addedItem.final_line_price ||
          addedItem.line_price ||
          addedItem.price * addedItem.quantity;
        this.#addToCartTotal(addedPrice);

        // Dispatch event with optimistic data
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: "addons-page",
            variantId: variantIdStr,
            itemCount: 1,
          }),
        );
      } else {
        // Cart data is fresh - use it normally
        this.#syncCartStateFromCart(cart);

        this.dispatchEvent(
          new CartAddEvent(cart || {}, this.id, {
            source: "addons-page",
            variantId: variantIdStr,
            itemCount: cart?.item_count ?? 1,
            cart: cart,
          }),
        );
      }
    } catch (error) {
      console.error("[AddonsPage] Failed to add to cart:", error);
      button.disabled = false;
    } finally {
      this.#loadingVariantId = null;
    }
  }

  /**
   * Decreases the quantity of an add-on
   * @param {{ variantId: string | number }} data
   * @param {Event} event
   */
  async decreaseQuantity({ variantId }, event) {
    // Ensure variantId is string (parseValue may convert to number)
    const variantIdStr = String(variantId);
    let addonData = this.#cartAddons.get(variantIdStr);

    // If we don't have the line key (e.g., stale initial fetch), fetch cart now
    if (!addonData) {
      await this.#syncCartState();
      addonData = this.#cartAddons.get(variantIdStr);
      if (!addonData) return;
    }

    const newQuantity = addonData.quantity - 1;
    await this.#updateQuantity(variantIdStr, addonData.lineKey, newQuantity);
  }

  /**
   * Increases the quantity of an add-on
   * @param {{ variantId: string | number }} data
   * @param {Event} event
   */
  async increaseQuantity({ variantId }, event) {
    // Ensure variantId is string (parseValue may convert to number)
    const variantIdStr = String(variantId);
    let addonData = this.#cartAddons.get(variantIdStr);

    // If we don't have the line key (e.g., stale initial fetch), fetch cart now
    if (!addonData) {
      await this.#syncCartState();
      addonData = this.#cartAddons.get(variantIdStr);
      if (!addonData) return;
    }

    if (addonData.quantity >= MAX_QTY_LIMIT) return;

    const newQuantity = addonData.quantity + 1;
    await this.#updateQuantity(variantIdStr, addonData.lineKey, newQuantity);
  }

  /**
   * Updates the quantity of a cart item
   * @param {string} variantId
   * @param {string} lineKey
   * @param {number} quantity
   */
  async #updateQuantity(variantId, lineKey, quantity) {
    if (this.#loadingVariantId) return;

    this.#loadingVariantId = variantId;
    this.#showQuantityLoader(variantId, true);

    try {
      const body = JSON.stringify({
        id: lineKey,
        quantity,
      });

      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig("json", { body }),
      );

      if (!response.ok) {
        throw new Error("Failed to update quantity");
      }

      // The /cart/change.js endpoint returns the full cart object
      // Use it directly instead of fetching /cart.js (avoids eventual consistency issues)
      const cart = await response.json();

      // Update UI with cart data from the change response
      this.#syncCartStateFromCart(cart);

      // Dispatch cart update event with cart so cart-icon reads correct item_count
      this.dispatchEvent(
        new CartUpdateEvent(cart || {}, this.id, {
          source: "addons-page",
          variantId,
          itemCount: cart?.item_count ?? 0,
          cart: cart,
        }),
      );
    } catch (error) {
      console.error("[AddonsPage] Failed to update quantity:", error);
    } finally {
      this.#loadingVariantId = null;
      this.#showQuantityLoader(variantId, false);
    }
  }

  /**
   * Shows/hides the quantity loader for a specific card
   * @param {string} variantId
   * @param {boolean} show
   */
  #showQuantityLoader(variantId, show) {
    const card = this.querySelector(
      `[data-addon-card][data-variant-id="${variantId}"]`,
    );
    if (!card) return;

    const loader = card.querySelector("[data-qty-loader]");
    const qtyDisplay = card.querySelector("[data-quantity-display]");

    if (loader instanceof HTMLElement) {
      loader.classList.toggle("hidden", !show);
    }
    if (qtyDisplay instanceof HTMLElement) {
      qtyDisplay.classList.toggle("opacity-50", show);
    }
  }

  // CART STATE SYNC

  /**
   * Fetches the current cart
   * @returns {Promise<Object|null>}
   */
  async #fetchCart() {
    try {
      // ACQ-210 #3 STATE: generic getter. Reads coherent cart-state (cache hit
      // when warm, network on a cold/invalidated cache). No force needed.
      return await getCart();
    } catch (error) {
      console.error("[AddonsPage] Failed to fetch cart:", error);
      return null;
    }
  }

  /**
   * Syncs the component state with the current cart (fetches /cart.js)
   * Used for initial page load and external cart updates
   * @param {Object} [options]
   * @param {boolean} [options.preserveTotalIfStale=false] - If true, don't update total when cart appears stale
   */
  async #syncCartState(options = {}) {
    const { preserveTotalIfStale = false } = options;

    // Prevent concurrent syncs - if already syncing, skip this call
    if (this.#isSyncing) return;

    this.#isSyncing = true;

    try {
      // ACQ-210 #4 STATE: page-load / external-update sync. cart:update invalidates
      // the shared cache before this fires, so a non-forced read returns fresh data;
      // a cold cache fetches on its own. Stale-detection below stays as the guard.
      const cart = await getCart();

      // Check if cart data appears stale (0 items but server rendered items)
      // This happens due to Shopify's eventual consistency issues
      const serverRenderedTotal = this.refs.cartTotal?.textContent?.trim();
      const hasServerRenderedTotal =
        serverRenderedTotal &&
        serverRenderedTotal !== "$0.00" &&
        serverRenderedTotal !== "$0";
      const cartAppearsStale =
        cart?.items?.length === 0 && hasServerRenderedTotal;

      if (preserveTotalIfStale && cartAppearsStale) {
        // Cart data is stale - only sync what we can trust
        // Skip updating total and card states since server-rendered state is correct
        return;
      }

      this.#syncCartStateFromCart(cart);
    } catch (error) {
      console.error("[AddonsPage] Failed to sync cart state:", error);
    } finally {
      this.#isSyncing = false;
    }
  }

  /**
   * Syncs the component state from a cart object (no fetch needed)
   * Use this when you already have fresh cart data from an API response
   * @param {Object} cart - Cart object with items array and total_price
   */
  #syncCartStateFromCart(cart) {
    if (!cart?.items) return;

    // Clear and rebuild cart addons map
    this.#cartAddons.clear();

    for (const item of cart.items) {
      const isOnetimeAddon = item.properties?._is_onetime_addon === "true";
      const isFreeAddon = item.properties?._is_free_addon === "true";

      if (isOnetimeAddon || isFreeAddon) {
        this.#cartAddons.set(item.variant_id.toString(), {
          lineKey: item.key,
          quantity: item.quantity,
        });
      }
    }

    // Update all card UIs
    this.#updateAllCardStates();

    // Update cart total
    this.#updateCartTotal(cart.total_price);
  }

  /**
   * Updates all card states based on cart data
   */
  #updateAllCardStates() {
    const cards = this.querySelectorAll("[data-addon-card]");

    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      const variantId = card.dataset.variantId;
      if (!variantId) return;

      const addonData = this.#cartAddons.get(variantId);
      const addButton = card.querySelector('[data-action="add-to-cart"]');
      const qtyWrapper = card.querySelector("[data-quantity-wrapper]");
      const qtyDisplay = card.querySelector("[data-quantity-display]");

      if (addonData) {
        // Show quantity controls, hide add button
        addButton?.classList.add("hidden");
        qtyWrapper?.classList.remove("hidden");

        if (qtyDisplay) {
          qtyDisplay.textContent = addonData.quantity.toString();
        }
      } else {
        // Show add button, hide quantity controls
        addButton?.classList.remove("hidden");
        qtyWrapper?.classList.add("hidden");

        // Re-enable button if it was disabled
        if (addButton instanceof HTMLButtonElement) {
          addButton.disabled = false;
        }
      }
    });
  }

  /**
   * Updates the cart total display
   * @param {number} totalPrice - Price in cents
   */
  #updateCartTotal(totalPrice) {
    const { cartTotal } = this.refs;
    if (!cartTotal) return;

    // Format price
    cartTotal.textContent = formatPrice(totalPrice);
  }

  /**
   * Adds an amount to the current cart total (optimistic update)
   * @param {number} priceInCents - Price to add in cents
   */
  #addToCartTotal(priceInCents) {
    const { cartTotal } = this.refs;
    if (!cartTotal) return;

    // Parse current total
    const currentText = cartTotal.textContent?.trim() || "$0.00";
    const currentCents = Math.round(
      parseFloat(currentText.replace(/[^0-9.-]/g, "")) * 100,
    );

    // Add and update
    const newTotal = currentCents + priceInCents;
    cartTotal.textContent = formatPrice(newTotal);
  }

  /**
   * Updates a single card's UI state based on cart data
   * @param {string} variantId
   */
  #updateCardState(variantId) {
    const card = this.querySelector(
      `[data-addon-card][data-variant-id="${variantId}"]`,
    );
    if (!(card instanceof HTMLElement)) return;

    const addonData = this.#cartAddons.get(variantId);
    const addButton = card.querySelector('[data-action="add-to-cart"]');
    const qtyWrapper = card.querySelector("[data-quantity-wrapper]");
    const qtyDisplay = card.querySelector("[data-quantity-display]");

    if (addonData) {
      // Show quantity controls, hide add button
      addButton?.classList.add("hidden");
      qtyWrapper?.classList.remove("hidden");

      if (qtyDisplay) {
        qtyDisplay.textContent = addonData.quantity.toString();
      }
    } else {
      // Show add button, hide quantity controls
      addButton?.classList.remove("hidden");
      qtyWrapper?.classList.add("hidden");

      // Re-enable button if it was disabled
      if (addButton instanceof HTMLButtonElement) {
        addButton.disabled = false;
      }
    }
  }

  /**
   * Handles cart update events from other sources
   * Uses grid-only morphing to preserve filter/sort UI
   * @param {CartUpdateEvent | CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.data?.source === "addons-page") return;

    // Use grid-only morph to check for gating and update products
    await this.#updateProductGridOnly();
  };

  // SEGMENT TOGGLE (Filters/Sort expand/collapse)
  /**
   * Toggles a collapsible segment (filters or sort)
   * @param {Event} event
   */
  toggleSegment(event) {
    // Find the actual toggle button (event.target might be SVG, path, or text inside)
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest("[data-segment-toggle]");
    if (!(button instanceof HTMLElement)) return;

    const segment = button.closest("[data-segment]");
    if (!(segment instanceof HTMLElement)) return;

    const isExpanded = segment.dataset.expanded === "true";

    // Toggle this segment (allow multiple to be open simultaneously)
    const newExpanded = !isExpanded;
    segment.dataset.expanded = newExpanded ? "true" : "false";
    button.setAttribute("aria-expanded", newExpanded ? "true" : "false");
  }

  /**
   * Waits for an element to appear within this component using MutationObserver
   * @param {string} selector - CSS selector to find
   * @param {number} maxWait - Maximum time to wait in ms
   * @returns {Promise<Element|null>}
   */
  async #waitForElement(selector, maxWait = 1000) {
    // Check if element already exists
    const existing = this.querySelector(selector);
    if (existing) return existing;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, maxWait);

      const observer = new MutationObserver(() => {
        const element = this.querySelector(selector);
        if (element) {
          clearTimeout(timeout);
          observer.disconnect();
          resolve(element);
        }
      });

      observer.observe(this, {
        childList: true,
        subtree: true,
      });
    });
  }

  // CHECKOUT
  /**
   * Proceeds to checkout
   */
  checkout() {
    window.location.href = "/checkout";
  }
}

if (!customElements.get("addons-page-component")) {
  customElements.define("addons-page-component", AddonsPageComponent);
}
